import { useSnackbar } from "notistack";
import { useContext, useEffect, useRef, useState } from "react";
import { useLayers } from "../contexts/LayerContext"; // Ensure this import is correct
import { SelectionContext } from "../contexts/SelectionContext";
import { useUser } from "../contexts/UserContext";
import { LegendLayerConfig, MapLibreLayerSpecification, MapLayerConfig } from "../types"; // Adjust the import path as necessary
import { apiFetch } from "../utilities/api";
import { transformToMapLibreLayer } from "../utilities/transformLayerConfigs";
import { enqueueUniqueSnackbar } from '../utilities/snackbarUtils';
import { isFulcrumLayer } from "../utilities/fulcrumLibService";
import { validateCloudFrontCookies, isCloudFrontAuthError } from "../utilities/cloudFrontAuth";
import { isEnvCdnUrl } from '../utilities/cdnUtils';

export const useMapLibreLayers = () => {
  const { layers, setLayers, layerRefreshTrigger } = useLayers(); // Add layerRefreshTrigger
  const { basicAuthHeader, oAuthHeader, getStoredToken, profile } = useUser();
  const [mapLibreLayers, setMapLibreLayers] = useState<
    MapLibreLayerSpecification[]
  >([]);
  const [mapLibreLayersReady, setMapLibreLayersReady] = useState(false);
  const { currentFilter, filterArea, selectedPrimaryFilterEntity } = useContext(SelectionContext);
  const loadedFiltersRef = useRef<Set<string>>(new Set());
  const isLoadingRef = useRef(false);
  const [errorLayers, setErrorLayers] = useState<Set<string>>(new Set());
  const { enqueueSnackbar } = useSnackbar();

  // useRef to track the previous layers dependency to detect significant changes
  const prevLayersRef = useRef<MapLayerConfig[]>();

  // Effect to handle layer refresh triggers
  useEffect(() => {
    // Check if any layers need to be refreshed
    const layersToRefresh = Object.keys(layerRefreshTrigger).filter(layerId => 
      layerRefreshTrigger[layerId] > 0
    );
    
    if (layersToRefresh.length === 0) return;
    
    console.log('[useMapLibreLayers] Layers to refresh:', layersToRefresh);
    
    // Since layers are loaded together in combined cache entries, 
    // we need to clear all cache to refresh specific layers
    // This is acceptable since layer loading is reasonably fast
    if (layersToRefresh.length > 0) {
      console.log('[useMapLibreLayers] Clearing entire layer cache to refresh:', layersToRefresh);
      loadedFiltersRef.current.clear();
      setMapLibreLayersReady(false);
      isLoadingRef.current = false; // Reset loading flag to allow reload
    }
  }, [layerRefreshTrigger]);

  useEffect(() => {
    // Reset internal states if the core layer definition changes significantly
    // This happens when the user logs in/out causing `layers` from context to change reference
    if (prevLayersRef.current !== layers) {
      console.debug('Core layers array changed, resetting mapLibreLayers state.');
      setMapLibreLayers([]);
      setMapLibreLayersReady(false);
      setErrorLayers(new Set());
      loadedFiltersRef.current = new Set(); // Clear the loaded filters cache
      isLoadingRef.current = false; // Reset loading flag in case it was stuck
    }
    // Update the ref for the next render
    prevLayersRef.current = layers;

    // Use either the structured entity name or fallback to the legacy filterArea
    const activeFilterValue = selectedPrimaryFilterEntity?.name || filterArea;
    // Use layer IDs joined together for a more specific dependency key
    const layerIds = layers.map(l => l.id).sort().join(','); 
    const depsKey = `${layerIds}-${!!basicAuthHeader}-${!!oAuthHeader}-${activeFilterValue || "initial"}`;

    // Skip if no OAuth header is available yet
    if (layers.some(layer => layer.useOAuth) && !oAuthHeader) {
      console.debug('Skipping layer load - waiting for auth');
      return;
    }

    if (loadedFiltersRef.current.has(depsKey)) return;
    if (isLoadingRef.current) return;

    // Set loading state at the beginning of the fetch process
    setMapLibreLayersReady(false);

    const fetchAndAddAuthToLayers = async () => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      // Note: errorLayers is reset above if layers changed, otherwise errors persist per session

      try {
        const transformedLayers: MapLibreLayerSpecification[] = [];

        for (const layer of layers.filter(
          (layer): layer is LegendLayerConfig => layer.type !== "raster-dem" // TODO: Excluding terrain layers... We load these directly in MapComponent
        )) {
          try {
            // Handle Fulcrum layers
            if (isFulcrumLayer(layer)) {
              try {
                // Use the service approach
                console.log(`Loading Fulcrum layer: ${layer.id}`, layer);
                // @ts-ignore - TS won't recognize the formId property
                const formId = layer.formId;
                
                // Use the direct API approach without requiring fulcrum-app library
                const { fetchFulcrumRecordsAsGeoJSON, createGeoJsonLayerConfig } = await import('../utilities/fulcrumLibService');
                
                // Fetch GeoJSON with proper authentication
                const geoJsonData = await fetchFulcrumRecordsAsGeoJSON(formId, profile);
                
                console.log(`Successfully loaded Fulcrum data for layer ${layer.id}`);
                
                // Create a properly typed GeoJSON layer config using the helper function
                const geoJsonLayer = createGeoJsonLayerConfig(layer, geoJsonData);
                
                // Process as a standard GeoJSON layer
                const mapLibreLayer = transformToMapLibreLayer(geoJsonLayer, geoJsonData);
                
                if (mapLibreLayer) {
                  transformedLayers.push(mapLibreLayer);
                }
                
                continue; // Skip to next layer
              } catch (error: any) { // Type the error as any to handle error.message safely
                console.error(`Error with data layer ${layer.id}:`, error);
                setErrorLayers(prev => new Set(prev).add(layer.id));
                
                // More user-friendly error messages for common Fulcrum API issues
                let errorMessage = `Failed to load ${layer.id}`;
                if (error?.message?.includes('Failed to fetch') || 
                    error?.message?.includes('503') ||
                    error?.message?.includes('CORS')) {
                  errorMessage = `${layer.id} temporarily unavailable (API issue)`;
                } else if (error?.message) {
                  errorMessage = `Failed to load ${layer.id}: ${error.message}`;
                }
                
                enqueueUniqueSnackbar(
                  enqueueSnackbar,
                  errorMessage,
                  {
                    variant: 'warning', // Changed from 'error' to 'warning' for API issues
                    autoHideDuration: 5000,
                  }
                );
                continue; // Skip this layer
              }
            }

            let data;
            const headers = {};

            if (layer.useBasicAuth && basicAuthHeader) {
              Object.assign(headers, basicAuthHeader);
            } else if (layer.useOAuth && oAuthHeader) {
              Object.assign(headers, oAuthHeader);
            }

            // @ts-ignore
            if (Object.keys(headers).length > 0 || layer.filters) {
              const options: RequestInit = { headers };
              
              if ("filters" in layer && layer.filters && layer.filters.length > 0) {
                options.method = "POST";
                options.headers = {
                  ...options.headers,
                  "Content-Type": "application/json",
                };
                options.body = currentFilter
                  ? JSON.stringify({ filter: currentFilter })
                  : JSON.stringify({ filter: layer.filters[0] });
              }

              try {
                if (layer.useOAuth) {
                  if (!oAuthHeader) {
                    console.debug('Skipping OAuth layer, no auth header yet:', layer.id);
                    continue;
                  }
                  
                  data = await apiFetch(layer.url, {
                    ...options,
                    credentials: 'include',
                    headers: options.headers as Record<string, string>
                  });
                } else {
                  const response = await fetch(layer.url, options);
                  if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                  }
                  data = await response.json();
                }
              } catch (error) {
                console.warn(`Failed to fetch layer ${layer.id}:`, error);
                setErrorLayers(prev => new Set(prev).add(layer.id));
                enqueueUniqueSnackbar(
                  enqueueSnackbar,
                  `Failed to load layer: ${layer.id}`,
                  {
                    variant: 'warning',
                    autoHideDuration: 3000,
                  }
                );
                continue; // Skip this layer and continue with others
              }
            } else {
              // For layers without OAuth/Basic auth, check if it's a CDN URL that needs cookies
              const cdnUrl = layer.url && typeof layer.url === 'string' && isEnvCdnUrl(layer.url);
              // @ts-ignore - Allow for optional useCookies property on layer config
              const needsCredentials = layer.useCookies || cdnUrl;
              
              if (needsCredentials && typeof layer.url === 'string') {
                try {
                  console.log(`Fetching CDN layer with credentials: ${layer.id}`);
                  const response = await fetch(layer.url, {
                    credentials: 'include', // Include cookies for CDN authentication
                  });
                  
                  if (!response.ok) {
                    console.warn(`CDN layer fetch failed for ${layer.id}: ${response.status} ${response.statusText}`);
                    
                    // Use proper CloudFront authentication error detection
                    if (response.status === 403) {
                      const errorText = await response.text().catch(() => 'Unknown error');
                      console.warn(`403 error details for ${layer.id}:`, errorText.substring(0, 200));
                      
                      // Use the robust CloudFront error detection
                      const isCloudFrontError = isCloudFrontAuthError(response, errorText);
                      
                      if (isCloudFrontError) {
                        // Validate CloudFront cookies properly
                        const cookieValidation = validateCloudFrontCookies();
                        
                        console.warn(`CloudFront authentication issue detected for ${layer.id}:`, {
                          cookieValidation,
                          errorSnippet: errorText.substring(0, 100)
                        });
                        
                        // Only log warning - don't automatically logout users
                        if (!cookieValidation.isValid) {
                          console.error(`CloudFront authentication problem for ${layer.id}: ${cookieValidation.details}`);
                          // Could potentially show a non-intrusive notification here
                          // but don't automatically log out the user
                        }
                      } else {
                        console.warn(`Non-CloudFront 403 error for ${layer.id} - likely server/permission issue`);
                      }
                    }
                    
                    // Don't throw error for failed layers - just mark as error and continue
                    throw new Error(`CDN layer ${layer.id} failed: ${response.status} ${response.statusText}`);
                  }
                  
                  data = await response.json();
                  console.log(`Successfully fetched CDN layer: ${layer.id}`);
                } catch (error) {
                  console.warn(`Failed to fetch CDN layer ${layer.id}:`, error);
                  setErrorLayers(prev => new Set(prev).add(layer.id));
                  
                  // Provide specific error message for expired cookies
                  let errorMessage = `Failed to load CDN layer: ${layer.id}`;
                  if (error instanceof Error && error.message.includes('CloudFront authentication expired')) {
                    errorMessage = `Session May Be Expired - try logging out and back in to refresh access`;
                  }
                  
                  enqueueUniqueSnackbar(
                    enqueueSnackbar,
                    errorMessage,
                    {
                      variant: 'warning',
                      autoHideDuration: 5000, // Longer duration for important messages
                    }
                  );
                  continue; // Skip this layer and continue with others
                }
              } else {
                // For other URLs, use the URL directly (no fetch needed)
                data = layer.url;
              }
            }

            // Take the layer definition, and its data that we have fetched with our oAuth etc, and transform it into a MapLibre Layer Specification ready to load on the map

            const mapLibreLayer = transformToMapLibreLayer(layer, data);

            if (mapLibreLayer) {
              transformedLayers.push(mapLibreLayer);
            }
          } catch (layerError) {
            console.warn(`Error processing layer ${layer.id}:`, layerError);
            setErrorLayers(prev => new Set(prev).add(layer.id));
            continue; // Skip this layer and continue with others
          }
        }

        console.debug('Layer fetch complete');
        loadedFiltersRef.current.add(depsKey);
        setMapLibreLayers(transformedLayers);
        setMapLibreLayersReady(true);
      } catch (error) {
        console.error('Error processing layers:', error);
        enqueueSnackbar('Error loading map layers', {
          variant: 'error',
          autoHideDuration: 4000,
        });
      } finally {
        isLoadingRef.current = false;
      }
    };

    if (!getStoredToken()) {
      // If no token, ensure we are in a clean state
      setMapLibreLayers([]);
      setMapLibreLayersReady(false);
      setErrorLayers(new Set());
      loadedFiltersRef.current = new Set();
      return;
    }

    fetchAndAddAuthToLayers();
  }, [layers, basicAuthHeader, oAuthHeader, filterArea, selectedPrimaryFilterEntity, layerRefreshTrigger]);

  // Separate effect for filter changes
  useEffect(() => {
    if (!currentFilter || !mapLibreLayersReady) return;
    // Handle filter changes here when needed
  }, [currentFilter, mapLibreLayersReady]);

  // Handle user logout by clearing layer processing state
  useEffect(() => {
    if (!profile) {
      console.log('[useMapLibreLayers] User logged out, clearing layer processing state');
      isLoadingRef.current = false; // Stop any ongoing processing
      setErrorLayers(new Set()); // Clear error layers
    }
  }, [profile]);

  // Fix dependencies to avoid infinite loops
  useEffect(() => {
    // Skip if no OAuth token is available yet for OAuth layers
    const hasOAuthLayers = layers.some(layer => layer.searchable && layer.useOAuth);
    if (hasOAuthLayers && !getStoredToken()) {
      console.debug('Skipping searchable layers fetch - waiting for auth');
      return;
    }

    // Process Fulcrum layers as early as possible
    // This will transform Fulcrum layers into GeoJSON layers with the proper data
    const processFulcrumLayers = async () => {
      if (isLoadingRef.current) return;
      
      // Early return if no user profile (e.g., during logout)
      if (!profile) {
        console.log('[useMapLibreLayers] No user profile available, skipping Fulcrum layer processing');
        return;
      }
      
      isLoadingRef.current = true;

      const fulcrumLayers = layers.filter(layer => layer.type === 'fulcrum');
      if (fulcrumLayers.length === 0) {
        isLoadingRef.current = false;
        return;
      }

      console.log(`Found ${fulcrumLayers.length} Fulcrum layers to process`);
      const updatedLayers = [...layers];
      let hasChanges = false;

      for (let i = 0; i < updatedLayers.length; i++) {
        const layer = updatedLayers[i];
        
        if (layer.type === 'fulcrum') {
          try {
            console.log(`Pre-processing Fulcrum layer: ${layer.id}`);
            
            // @ts-ignore - TS won't recognize the formId property
            const formId = layer.formId;
            
            // Use the direct API approach
            const { fetchFulcrumRecordsAsGeoJSON, createGeoJsonLayerConfig } = await import('../utilities/fulcrumLibService');
            
            // Fetch GeoJSON with proper authentication
            const geoJsonData = await fetchFulcrumRecordsAsGeoJSON(formId, profile);
            
            console.log(`Successfully pre-processed Fulcrum data for layer ${layer.id}`);
            
            // Create a properly typed GeoJSON layer
            const geoJsonLayer = createGeoJsonLayerConfig(layer, geoJsonData);
            
            // Replace the Fulcrum layer with a GeoJSON layer
            updatedLayers[i] = geoJsonLayer;
            hasChanges = true;
          } catch (error: any) {
            console.error(`Error pre-processing Fulcrum layer ${layer.id}:`, error);
            setErrorLayers(prev => new Set(prev).add(layer.id));
            
            // Mark the layer as having an error
            updatedLayers[i] = { ...layer, error: true };
            hasChanges = true;
          }
        }
      }

      // Update the layers array if we made changes
      if (hasChanges) {
        setLayers(updatedLayers);
      }
      
      isLoadingRef.current = false;
    };

    // Only run this effect if we have Fulcrum layers
    if (layers.some(layer => layer.type === 'fulcrum')) {
      processFulcrumLayers();
    }
  }, [getStoredToken, layers, setLayers, setErrorLayers, profile]);

  return {
    mapLibreLayers,
    mapLibreLayersReady,
    errorLayers,
  };
};
